/* ===== assets/custom-cart-discount.js ===== */
(() => {
  /* ---------- Shared cart promise (create once, stays fresh) ---------- */
  if (!window.__cartPromise) {
    const makeCartPromise = () => new Promise(async (resolve) => {
      try {
        const res = await fetch(`${Shopify.routes.root}cart.js`, { headers: { 'Accept': 'application/json' }});
        resolve(await res.json());
      } catch {
        resolve({}); // fail-soft
      }
    });

    window.__cartPromise = makeCartPromise();

    const refreshCartPromise = (cart) => {
      window.__cartPromise = cart ? Promise.resolve(cart) : makeCartPromise();
    };

    document.addEventListener('cart:refresh', (e) => refreshCartPromise(e?.detail?.cart));
    document.addEventListener('cart:change',  (e) => refreshCartPromise(e?.detail?.cart));
    document.addEventListener('cart:updated', (e) => refreshCartPromise(e?.detail?.cart));
    window.addEventListener('pageshow', (e) => { if (e.persisted) window.__cartPromise = makeCartPromise(); });
  }

  /* ---------- Helpers ---------- */
  const isCartPage = () => !!(window.themeVariables?.settings?.pageType === 'cart' || window.location.pathname.replace(/\/+$/, '') === '/cart');
  const hardReload  = () => window.location.reload();

  const dispatchCartEvents = (cart) => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart }}));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }}));
  };

  const toLower = (s) => (s || '').toString().trim().toLowerCase();

  /* ---------- Base class ---------- */
  class AbstractCartDiscount extends HTMLElement {
    /** Returnerer nuværende anvendte koder som lowercase strenge (applicable==true) */
    async getAppliedCodes() {
      const cart = await window.__cartPromise;
      const list = cart?.discount_codes || [];
      // Tillad både string og {code, applicable}
      return list
        .map(d => typeof d === 'string' ? d : (d?.code ?? d?.title ?? ''))
        .filter(Boolean)
        .map(toLower)
        .filter((_, i, arr) => arr.indexOf(_) === i);
    }

    /**
     * Sender {discount: "code1,code2"} til /cart/update.js
     * - Returnerer cart-json
     * - Sender events altid (så drawer m.m. opdateres)
     * - Kaster kun ved netværksfejl eller HTTP-fejl
     * - Invalid codes markeres i json; vi kaster *efter* events, så UI stadig opdateres
     */
    async postDiscount(discountCsv) {
      const body = JSON.stringify({ discount: (discountCsv || '') });
      const resp = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        keepalive: true,
        body
      });
      if (!resp.ok) throw new Error('update_failed');

      const json = await resp.json();
      dispatchCartEvents(json);

      // Notér evt. ugyldige koder (applicable:false) – UI kan vise banner
      const hasInvalid = Array.isArray(json?.discount_codes)
        && json.discount_codes.some(obj => obj?.applicable === false);

      if (hasInvalid) {
        const err = new Error('invalid_code');
        err.cart = json;
        throw err;
      }
      return json;
    }

    /** Foreslår reload når vi er på /cart (server-rendered totals/badges) */
    reloadIfCartPage() { if (isCartPage()) hardReload(); }

    /** Viser global fejl-banner-komponent hvis tilstede */
    showErrorBanner() {
      document.dispatchEvent(new CustomEvent('cart:discount:error'));
    }
  }

  /* ---------- (Valgfrit) Error banner host ---------- */
  class CartDiscountBanner extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      document.addEventListener('cart:discount:error', () => { this.hidden = false; }, { signal: this._ac.signal });
    }
    disconnectedCallback() { this._ac?.abort(); }
  }

  /* ---------- Inputfelt: Enter/blur apply + hidden akkumulator ---------- */
  class CartDiscountField extends AbstractCartDiscount {
    connectedCallback() {
      // Hidden akkumulator (kræver name="discount" for at fungere i forms)
      this._hidden = this.querySelector('input[type="hidden"][name="discount"]');
      // Synligt input (alt ikke-hidden)
      this._input  = this.querySelector('input:not([type="hidden"])');
      this._loader = this.querySelector('.cart-discount-field__loader');

      this._originalHiddenValue = this._hidden?.value || '';

      // Hold hidden i sync mens der skrives
      this.addEventListener('input', () => {
        if (!this._hidden || !this._input) return;
        const typed = this._input.value || '';
        const parts = [this._originalHiddenValue, typed].filter(v => v && v.trim() !== '');
        this._hidden.value = parts.join(',');
      });

      // blur/change → apply
      this.addEventListener('change', () => this.applyCode());

      // Enter → apply
      if (this._input) {
        this._input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); this.applyCode(); }
        });
      }
    }

    setLoading(state) {
      if (this._loader) this._loader.hidden = !state;
      if (this._input)  this._input.disabled = state;
    }

    async applyCode() {
      const inputVal = (this._input?.value || '').trim();
      if (!inputVal) return;

      this.setLoading(true);
      try {
        // Sammensæt nuværende + ny (case-insensitive dedupe)
        const applied = await this.getAppliedCodes(); // lowercase
        const newCode = toLower(inputVal);
        const next = applied.includes(newCode) ? applied : [...applied, newCode];

        await this.postDiscount(next.join(','));

        // Nulstil felt (så man kan prøve en anden kode)
        if (this._input)  this._input.value = '';
        if (this._hidden) this._hidden.value = this._originalHiddenValue || '';

        // Server-renderede totals på /cart kræver reload
        this.reloadIfCartPage();
      } catch (err) {
        // Vis fejlbanner, men behold evt. drawer-opdatering via events i postDiscount
        this.showErrorBanner();
      } finally {
        this.setLoading(false);
      }
    }
  }

  /* ---------- Fjern-knap for enkel kode ---------- */
  class CartDiscountRemoveButton extends AbstractCartDiscount {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        // Undgå at link <button> inde i badge submitter formularer
        e.preventDefault();
        e.stopPropagation();
        this.removeCode();
      });
    }

    _setBusy(state) {
      this.setAttribute('aria-busy', state ? 'true' : 'false');
      // Disable evt. indre knap for bedre a11y/UX
      const btn = this.querySelector('button, [role="button"]');
      if (btn) btn.disabled = !!state;
    }

    async removeCode() {
      this._setBusy(true);
      try {
        const toRemove = toLower(this.getAttribute('discount-code') || '');
        if (!toRemove) { this._setBusy(false); return; }

        const applied = await this.getAppliedCodes(); // lowercase
        const remaining = applied.filter(c => c !== toRemove);

        await this.postDiscount(remaining.join(','));
        this.reloadIfCartPage();
      } catch {
        this.showErrorBanner();
      } finally {
        this._setBusy(false);
      }
    }
  }

  /* ---------- Fjern-ALLE-koder knap (valgfri, hvis du tilføjer i UI) ---------- */
  class CartDiscountClearAllButton extends AbstractCartDiscount {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clearAll();
      });
    }

    _setBusy(state) {
      this.setAttribute('aria-busy', state ? 'true' : 'false');
      const btn = this.querySelector('button, [role="button"]');
      if (btn) btn.disabled = !!state;
    }

    async clearAll() {
      this._setBusy(true);
      try {
        await this.postDiscount(''); // tom streng → fjern alle koder
        this.reloadIfCartPage();
      } catch {
        this.showErrorBanner();
      } finally {
        this._setBusy(false);
      }
    }
  }

  /* ---------- Define custom elements (once) ---------- */
  if (!customElements.get('cart-discount-field'))          customElements.define('cart-discount-field', CartDiscountField);
  if (!customElements.get('cart-discount-remove-button'))  customElements.define('cart-discount-remove-button', CartDiscountRemoveButton);
  if (!customElements.get('cart-discount-clear-all'))      customElements.define('cart-discount-clear-all', CartDiscountClearAllButton);
  if (!customElements.get('cart-discount-banner'))         customElements.define('cart-discount-banner', CartDiscountBanner);

  /* ---------- Global Enter/Change delegation (shadow-safe) ---------- */
  if (!window.__discountHandlersBound) {
    window.__discountHandlersBound = true;

    const findFieldFromEvent = (e) => {
      // 1) Shadow-safe via composedPath
      const path = (typeof e.composedPath === 'function') ? e.composedPath() : null;
      if (Array.isArray(path)) {
        const el = path.find(n => n && n.nodeType === 1 && n.tagName && n.tagName.toLowerCase() === 'cart-discount-field');
        if (el) return el;
      }
      // 2) Fallback via DOM
      const t = e.target instanceof HTMLElement ? e.target : null;
      if (t) {
        const viaClosest = t.closest('cart-discount-field');
        if (viaClosest) return viaClosest;
        const block = t.closest('.cart-discount');
        if (block) return block.querySelector('cart-discount-field');
      }
      return null;
    };

    // ENTER: anvend når man skriver i et inputfelt
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      const t = e.target;
      if (!(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) return;

      const fieldEl = findFieldFromEvent(e);
      if (!fieldEl) return;

      e.preventDefault();
      e.stopPropagation();

      if (typeof fieldEl.applyCode === 'function') {
        fieldEl.applyCode();
      } else {
        fieldEl.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }
    }, true);

    // CHANGE: sikrer blur-adfærd, hvis change ikke bobler korrekt
    document.addEventListener('change', function (e) {
      const fieldEl = findFieldFromEvent(e);
      if (!fieldEl) return;
      if (typeof fieldEl.applyCode === 'function') fieldEl.applyCode();
    }, true);
  }
})();